/* eslint-disable no-console */
import {
  Box,
  Checkbox,
  FormControl,
  FormHelperText,
  FormLabel,
  HStack,
  Input,
  VStack,
} from '@chakra-ui/react';
import React, { useEffect, useState } from 'react';

import {
  finleyAssert,
  finleyAssertUnreachable,
} from '@finley-technologies/common';
import {
  CreditAgreement,
  DataTapeField,
} from '@finley-technologies/finley-types';

import { ConcentrationCalcFieldLabel } from './ConcentrationCalcFieldLabel';
import { FieldSelector } from './FieldSelector';
import { makeSelectOptions } from './makeSelectOptions';
import {
  AddIconButton,
  DeleteIconButton,
} from 'packages/common-frontend/components/IconButton';
import { Select } from 'packages/common-frontend/components/Select';
import { IntegerInputField } from '~/packages/creditAgreements/ui/CreditAgreementFullArtifact/edit/ConcentrationLimitEditor/IntegerInputField';

type OperationType = CreditAgreement.Config.ConcentrationLimit.Calculation.Filters.OperationType;
type FilterConfig = CreditAgreement.Config.ConcentrationLimit.Calculation.Filters.FilterConfig;
type OrderByConfig = CreditAgreement.Config.ConcentrationLimit.Calculation.Filters.OrderByConfig;
type EqualsConfig = CreditAgreement.Config.ConcentrationLimit.Calculation.Filters.EqualsConfig;
type OrderBySumConfig = CreditAgreement.Config.ConcentrationLimit.Calculation.Filters.OrderBySumConfig;
type CompareNumbersConfig = CreditAgreement.Config.ConcentrationLimit.Calculation.Filters.CompareNumbersConfig;
type InListConfig = CreditAgreement.Config.ConcentrationLimit.Calculation.Filters.InListConfig;
type EqualOperator = CreditAgreement.Config.ConcentrationLimit.Calculation.Filters.EqualOperator;
type CompareOperator = CreditAgreement.Config.ConcentrationLimit.Calculation.Filters.CompareOperator;
type OrderByOperator = CreditAgreement.Config.ConcentrationLimit.Calculation.Filters.OrderByOperator;

// returns an error message to render if given value is invalid for the given field.
type ValueError = {
  errorMessage: string;
  value: null;
};
const makeValueError = (errorMessage: string): ValueError => ({
  errorMessage,
  value: null,
});

type ValueSuccess = {
  errorMessage: null;
  value: string | number | boolean;
};
const makeValueSuccess = (value: string | number | boolean): ValueSuccess => ({
  errorMessage: null,
  value,
});
const convertValueToType = (
  value: any,
  fieldType: DataTapeField.Type,
): ValueSuccess | ValueError => {
  switch (fieldType) {
    case 'integer':
      return Number.isInteger(Number(value))
        ? makeValueSuccess(Number(value))
        : makeValueError(`[value:${value}] must be integer`);
    case 'float':
      return !Number.isNaN(Number(value))
        ? makeValueSuccess(Number(value))
        : makeValueError(`[value:${value}] must be float`);
    case 'string':
      return typeof value === 'string'
        ? makeValueSuccess(value)
        : makeValueError(`[value:${value}] must be string`);
    case 'boolean':
      return value === 'true' || value === 'false'
        ? makeValueSuccess(value === 'true')
        : makeValueError(`[value:${value}] must be boolean`);
    default:
      return makeValueError(`[fieldType:${fieldType}] not supported`);
  }
};

const convertValueEntered = (
  field: string,
  value: string,
  availableFields: CreditAgreement.Config.ConcentrationLimit.Calculation.AvailableField[],
): ValueError | ValueSuccess => {
  // @dual_cc:todo: validate value type based on field type.
  const fieldWithType = availableFields.find(
    (f) => f.fullyQualifiedFieldName === field,
  );
  if (!fieldWithType) {
    return makeValueError(`[field:${field} not found]`);
  }
  const { fieldType } = fieldWithType;
  return convertValueToType(value, fieldType);
};

type ListOfValuesError = {
  errorMessage: string;
  list: null;
};
const makeListOfValuesError = (errorMessage: string): ListOfValuesError => ({
  errorMessage,
  list: null,
});

type ListOfValueSuccess<T> = {
  errorMessage: null;
  list: T[];
};
const makeListOfValuesSuccess = <T,>(list: T[]): ListOfValueSuccess<T> => ({
  errorMessage: null,
  list,
});

// verifies that the given list of values is valid for the given field.
const convertListOfValues = (
  field: string,
  list: any[],
  availableFields: CreditAgreement.Config.ConcentrationLimit.Calculation.AvailableField[],
):
  | ListOfValuesError
  | ListOfValueSuccess<number>
  | ListOfValueSuccess<string>
  | ListOfValueSuccess<boolean> => {
  const fieldWithType = availableFields.find(
    (f) => f.fullyQualifiedFieldName === field,
  );
  if (!fieldWithType) {
    return makeListOfValuesError(`[field:${field} not found]`);
  }

  // now ensure that all values from the list can be converted to the field type.
  const { fieldType } = fieldWithType;
  const outputList = [];
  for (const listValue of list) {
    const { errorMessage, value } = convertValueToType(listValue, fieldType);
    if (errorMessage) {
      return makeListOfValuesError(errorMessage);
    }
    finleyAssert(value !== null, 'value should not be null');
    outputList.push(value);
  }
  // at this point we know that outputList is either list has all numbers or strings or booleans.
  // @ts-ignore
  return makeListOfValuesSuccess(outputList);
};

const mapFieldType = (
  field: string,
  availableFields: CreditAgreement.Config.ConcentrationLimit.Calculation.AvailableField[],
): 'number' | 'string' | 'boolean' => {
  const fieldWithType = availableFields.find(
    (f) => f.fullyQualifiedFieldName === field,
  );
  finleyAssert(fieldWithType, `getFieldType: [field:${field} not found`);

  const unSupportedTypes = [
    'date',
    'optionalDate',
    'dateTime',
    'optionalDateTime',
    'currency',
    'percentage',
    'optionalCurrency',
    'optionalPercentage',
  ];

  const { fieldType } = fieldWithType;
  finleyAssert(
    !unSupportedTypes.includes(fieldType),
    `getFieldType: unsupported type:${fieldWithType.fieldType}`,
  );
  switch (fieldType) {
    case 'integer':
    case 'optionalInteger':
    case 'float':
    case 'optionalFloat':
      return 'number';

    case 'string':
    case 'optionalString':
      return 'string';

    case 'boolean':
    case 'optionalBoolean':
      return 'boolean';

    // not supported, but we cant assert here because JSON editor allows it.
    case 'date':
    case 'optionalDate':
    case 'dateTime':
    case 'optionalDateTime':
    case 'currency':
    case 'percentage':
    case 'optionalCurrency':
    case 'optionalPercentage':
    default:
      // // cant assert here, because JSON editor allows it.
      return 'string';
  }
};

const createNewFilter = (operation: OperationType): FilterConfig => {
  const placeHolderFieldName = 'placeHolder field';
  const placeHolderStringValue = 'placeHolder Value';
  const placeHolderNumberValue = 42;
  switch (operation) {
    case 'orderBy':
      return {
        operation,
        field: placeHolderFieldName,
        order: 'asc',
        type: 'number',
        count: 0,
      };
    case 'orderBySum':
      return {
        operation,
        field: placeHolderFieldName,
        type: 'number',
        groupBy: placeHolderFieldName,
        count: 0,
        offset: 0,
      };
    case 'equals':
      return {
        operation,
        field: placeHolderFieldName,
        type: 'string',
        value: placeHolderStringValue,
      };
    case 'notEqual':
      return {
        operation,
        field: placeHolderFieldName,
        value: placeHolderStringValue,
        type: 'string',
      };
    case 'lessThan':
      return {
        operation,
        field: placeHolderFieldName,
        value: placeHolderNumberValue,
        type: 'number',
      };
    case 'lessThanOrEqual':
      return {
        operation,
        field: placeHolderFieldName,
        value: placeHolderNumberValue,
        type: 'number',
      };
    case 'greaterThan':
      return {
        operation,
        field: placeHolderFieldName,
        value: placeHolderNumberValue,
        type: 'number',
      };
    case 'greaterThanOrEqual':
      return {
        operation,
        field: placeHolderFieldName,
        value: placeHolderNumberValue,
        type: 'number',
      };
    case 'inList':
      return {
        operation,
        field: placeHolderFieldName,
        list: [],
        type: 'string',
      };
    case 'notInList':
      return {
        operation,
        field: placeHolderFieldName,
        list: [],
        type: 'string',
      };
    default:
      // this is okay to be assert.
      return finleyAssertUnreachable(operation);
  }
};

const OrderByOptions = makeSelectOptions(
  CreditAgreement.Config.ConcentrationLimit.Calculation.Filters
    .OrderByOperatorIterable,
);

type OrderBySumConfigEditorProps = {
  filter: OrderBySumConfig;
  availableFields: CreditAgreement.Config.ConcentrationLimit.Calculation.AvailableField[];
  onUpdate: (config: OrderBySumConfig) => void;
};
const OrderBySumConfigEditor = ({
  filter,
  availableFields,
  onUpdate,
}: OrderBySumConfigEditorProps) => {
  const [config, setConfig] = useState<OrderBySumConfig>(filter);

  const sumUpFieldAllowedTypes: DataTapeField.Type[] = ['float', 'integer'];
  const groupByFieldAllowedTypes: DataTapeField.Type[] = [
    'string',
    'integer',
    // TODO: we should allow to groupBy `boolean`, but our current calc-agent does not allow it.
  ];
  useEffect(() => {
    setConfig(filter);
  }, [filter]);

  const updateConfig = (newConfig: OrderBySumConfig) => {
    setConfig(newConfig);
    onUpdate(newConfig);
  };
  const setField = (field: string) => {
    const type = mapFieldType(field, availableFields);

    if (type === 'number') {
      updateConfig({ ...config, field });
    }
    finleyAssert(type !== 'boolean', 'boolean type not supported');
  };

  const setGroupByField = (field: string) => {
    const type = mapFieldType(field, availableFields);
    if (type === 'string' || type === 'number') {
      // we check instead of assert because of JSEditor, which
      // allows invalid values.
      updateConfig({ ...config, groupBy: field, type });
    }
  };

  // generate text to describe this filter.
  const generateFilterText = () => {
    const { field, groupBy, count, offset } = config;
    return `Group receivables by ${groupBy} and sum up ${field} for top ${count} group(s) after skipping first ${
      offset ?? '0'
    } group(s)`;
  };

  return (
    <Box p={4}>
      <FormControl>
        <ConcentrationCalcFieldLabel
          mb={1}
          label="Field"
          tooltip="Field to sum up"
        />

        <FieldSelector
          availableFields={availableFields}
          allowedTypes={sumUpFieldAllowedTypes}
          initialField={config.field}
          onSelectedFieldChange={(field) => setField(field)}
        />
      </FormControl>
      <FormControl mt={4}>
        <ConcentrationCalcFieldLabel
          mb={1}
          label="Group By"
          tooltip="Field by which receivables will be grouped"
        />

        <FieldSelector
          availableFields={availableFields}
          allowedTypes={groupByFieldAllowedTypes}
          initialField={config.groupBy}
          onSelectedFieldChange={(field) => setGroupByField(field)}
        />
      </FormControl>

      <HStack spacing={4} mt={4}>
        <FormControl flex="1">
          <ConcentrationCalcFieldLabel
            mb={1}
            label="Count"
            tooltip="Count of top groups to pick"
          />
          <IntegerInputField
            value={config.count || 0}
            onChange={(count) => {
              updateConfig({
                ...config,
                count,
              });
            }}
          />
        </FormControl>

        <FormControl flex="1">
          <ConcentrationCalcFieldLabel
            mb={1}
            label="Offset"
            tooltip="Number of top groups to ignore"
          />
          <IntegerInputField
            value={config.offset || 0}
            onChange={(offset) => {
              updateConfig({
                ...config,
                offset,
              });
            }}
          />
        </FormControl>
      </HStack>
      <FormHelperText mt={5}>{generateFilterText()}</FormHelperText>
    </Box>
  );
};

type OrderByConfigEditorProps = {
  filter: OrderByConfig;
  availableFields: CreditAgreement.Config.ConcentrationLimit.Calculation.AvailableField[];
  onUpdate: (config: OrderByConfig) => void;
};
const OrderByConfigEditor = ({
  filter,
  availableFields,
  onUpdate,
}: OrderByConfigEditorProps) => {
  const [config, setConfig] = useState<OrderByConfig>(filter);

  useEffect(() => {
    setConfig(filter);
  }, [filter]);

  const updateConfig = (newConfig: OrderByConfig) => {
    setConfig(newConfig);
    onUpdate(newConfig);
  };

  const setField = (field: string) => {
    const type = mapFieldType(field, availableFields);
    finleyAssert(type === 'number', 'OrderBy requires number type');
    updateConfig({ ...config, field, type });
  };

  // generate text to describe this filter.
  const generateFilterText = () => {
    const { field, order, count } = config;

    // if order is undefined we default to 'descending'.
    const effectiveOrder = order === 'asc' ? 'ascending' : 'descending';
    return `Order receivables by ${field} in ${effectiveOrder} order and pick top ${count} receivable(s)`;
  };

  return (
    <Box p={4}>
      <FormControl>
        <ConcentrationCalcFieldLabel
          mb={1}
          label="Field"
          tooltip="This field value will be used to sort receivables"
        />
        <FieldSelector
          availableFields={availableFields}
          allowedTypes={['float', 'integer']}
          initialField={config.field}
          onSelectedFieldChange={(field) => setField(field)}
        />
      </FormControl>
      <Select<OrderByOperator>
        value={config.order || 'desc'}
        onChange={(val) => {
          if (val) {
            updateConfig({ ...config, order: val });
          }
        }}
        options={OrderByOptions}
        width="sm"
      />
      <HStack spacing={4} mt={4}>
        <FormControl flex="1">
          <ConcentrationCalcFieldLabel
            mb={1}
            label="count"
            tooltip="count of top receivables to pick"
          />
          <IntegerInputField
            value={config.count || 0}
            onChange={(count) => {
              updateConfig({
                ...config,
                count,
              });
            }}
          />
        </FormControl>
      </HStack>
      <FormHelperText mt={5}>{generateFilterText()}</FormHelperText>
    </Box>
  );
};

type EqualsConfigEditorProps = {
  filter: EqualsConfig;
  availableFields: CreditAgreement.Config.ConcentrationLimit.Calculation.AvailableField[];
  onUpdate: (config: EqualsConfig) => void;
};
const EqualsConfigEditor = ({
  filter,
  availableFields,
  onUpdate,
}: EqualsConfigEditorProps) => {
  const [config, setConfig] = useState<EqualsConfig>(filter);
  const [currentValueError, setCurrentValueError] = useState<string | null>(
    null,
  );

  useEffect(() => {
    setConfig(filter);
  }, [filter]);

  const currentValue = config.value;
  const currentField = config.field;
  const currentOperation = config.operation;

  const updateConfig = (newConfig: EqualsConfig) => {
    const { errorMessage, value } = convertValueEntered(
      newConfig.field,
      newConfig.value,
      availableFields,
    );

    const updatedConfig = {
      ...newConfig,
      value,
    };
    setConfig(updatedConfig);
    setCurrentValueError(errorMessage);
    if (!errorMessage) {
      onUpdate(updatedConfig);
    }
  };

  const setValue = (value: string) => {
    updateConfig({ ...config, value });
  };

  const setField = (field: string) => {
    const type = mapFieldType(field, availableFields);
    updateConfig({ ...config, field, type });
  };
  const setOperation = (operation: EqualOperator) => {
    updateConfig({ ...config, operation });
  };

  const operationOptions = makeSelectOptions(
    CreditAgreement.Config.ConcentrationLimit.Calculation.Filters
      .EqualOperatorIterable,
  );

  // generate text to describe this filter.
  const generateFilterText = () => {
    const { field, value, operation } = config;
    return `Filter receivables where ${field} ${operation} ${value}`;
  };

  return (
    <Box p={4}>
      {currentValueError && <Box color="red">{currentValueError}</Box>}
      <FormControl>
        <ConcentrationCalcFieldLabel
          mb={1}
          label="Field"
          tooltip="field value to compare"
        />

        <FieldSelector
          availableFields={availableFields}
          allowedTypes={['float', 'integer', 'string', 'boolean']}
          initialField={currentField}
          onSelectedFieldChange={(field) => setField(field)}
        />
      </FormControl>

      <Select<EqualOperator>
        value={currentOperation}
        onChange={(val) => {
          if (val) {
            setOperation(val);
          }
        }}
        options={operationOptions}
        width="sm"
      />

      <HStack spacing={4} mt={4}>
        <FormControl flex="1">
          <ConcentrationCalcFieldLabel
            mb={1}
            label="value"
            tooltip="given value to compare against"
          />

          <Input
            id="value"
            name="value"
            value={currentValue}
            onChange={(event) => setValue(event.target.value)}
          />
        </FormControl>
      </HStack>
      <FormHelperText mt={5}>{generateFilterText()}</FormHelperText>
    </Box>
  );
};

type LessThanOrEqualConfigEditorProps = {
  filter: CompareNumbersConfig;
  availableFields: CreditAgreement.Config.ConcentrationLimit.Calculation.AvailableField[];
  onUpdate: (config: CompareNumbersConfig) => void;
};
const LessThanOrEqualConfigEditor = ({
  filter,
  availableFields,
  onUpdate,
}: LessThanOrEqualConfigEditorProps) => {
  const [config, setConfig] = useState<CompareNumbersConfig>(filter);

  useEffect(() => {
    setConfig(filter);
  }, [filter]);

  const currentValue = config.value;
  const currentField = config.field;
  const currentOperation = config.operation;

  const updateConfig = (newConfig: CompareNumbersConfig) => {
    setConfig(newConfig);
    onUpdate(newConfig);
  };

  const setValue = (value: string) => {
    const numberValue = Number(value);
    if (Number.isNaN(numberValue)) {
      return;
    }
    updateConfig({ ...config, value: numberValue });
  };
  const setField = (field: string) => {
    updateConfig({ ...config, field });
  };
  const setOperation = (operation: CompareOperator) => {
    updateConfig({ ...config, operation });
  };

  // generate text to describe this filter.
  const generateFilterText = () => {
    const { field, value, operation } = config;
    return `Filter receivables where ${field} is ${operation} ${value}`;
  };

  const operationOptions = makeSelectOptions(
    CreditAgreement.Config.ConcentrationLimit.Calculation.Filters
      .CompareOperatorIterable,
  );
  return (
    <Box p={4}>
      <FormControl>
        <ConcentrationCalcFieldLabel
          mb={1}
          label="Field"
          tooltip="field to compare"
        />

        <FieldSelector
          availableFields={availableFields}
          allowedTypes={['integer', 'float']}
          initialField={currentField}
          onSelectedFieldChange={(field) => setField(field)}
        />
      </FormControl>

      <Select<CompareOperator>
        value={currentOperation}
        onChange={(val) => {
          if (val) {
            setOperation(val);
          }
        }}
        options={operationOptions}
        width="sm"
      />

      <HStack spacing={4} mt={4}>
        <FormControl flex="1">
          <ConcentrationCalcFieldLabel
            mb={1}
            label="Value"
            tooltip="value to compare against"
          />
          <Input
            id="value"
            name="value"
            value={currentValue}
            onChange={(event) => setValue(event.target.value)}
          />
        </FormControl>
      </HStack>
      <FormHelperText mt={5}>{generateFilterText()}</FormHelperText>
    </Box>
  );
};

type InListConfigEditorProps = {
  filter: InListConfig;
  availableFields: CreditAgreement.Config.ConcentrationLimit.Calculation.AvailableField[];
  onUpdate: (config: InListConfig) => void;
};
const InListConfigEditor = ({
  filter,
  availableFields,
  onUpdate,
}: InListConfigEditorProps) => {
  const [config, setConfig] = useState<InListConfig>(filter);
  const [currentValueError, setCurrentValueError] = useState<string | null>(
    null,
  );

  useEffect(() => {
    setConfig(filter);
  }, [filter]);

  const currentField = config.field;
  const currentOperation = config.operation;
  const currentValue = config.list;

  const updateConfig = (newConfig: InListConfig) => {
    const { errorMessage, list } = convertListOfValues(
      newConfig.field,
      [...newConfig.list],
      availableFields,
    );
    setConfig({
      ...newConfig,

      // when list of values could not be converted
      // errorMessages will be set, but list will be null
      // we do want to display original list in that case hence
      list: list || [...newConfig.list],
    });
    setCurrentValueError(errorMessage);
    if (!currentValueError) {
      onUpdate(newConfig);
    }
  };

  const setField = (field: string) => {
    const type = mapFieldType(field, availableFields);
    updateConfig({ ...config, field, type });
  };

  const setOperation = (operation: InListOperator) => {
    updateConfig({ ...config, operation });
  };

  const setValue = (value: string[]) => {
    // @dual_cc:todo: ensure that value type is converted depending on
    // the field type.
    updateConfig({ ...config, list: value });
  };

  const generateFilterText = () => {
    const { field, list, operation } = config;
    return `Filter receivables where ${field} is ${
      operation === 'inList' ? 'one of' : 'not one of'
    } [${list.join(',')}]`;
  };

  const operationOptions = makeSelectOptions(
    CreditAgreement.Config.ConcentrationLimit.Calculation.Filters
      .InListConfigIterable,
  );
  type InListOperator = CreditAgreement.Config.ConcentrationLimit.Calculation.Filters.InListOperator;
  return (
    <Box p={4}>
      {currentValueError && <Box color="red">{currentValueError}</Box>}
      <FormControl>
        <ConcentrationCalcFieldLabel
          mb={1}
          label="Field"
          tooltip="Field to compare against"
        />

        <FieldSelector
          availableFields={availableFields}
          allowedTypes={['string', 'integer', 'boolean']}
          initialField={currentField}
          onSelectedFieldChange={(field) => setField(field)}
        />
      </FormControl>

      <Select<InListOperator>
        value={currentOperation}
        onChange={(val) => {
          if (val) {
            setOperation(val);
          }
        }}
        options={operationOptions}
        width="sm"
      />

      <HStack spacing={4} mt={4}>
        <FormControl flex="1">
          <ConcentrationCalcFieldLabel
            mb={1}
            label="Value"
            tooltip="list of values separated by comma"
          />

          <Input
            id="value"
            name="value"
            value={currentValue.join(',')} // @dual_cc:todo
            onChange={(event) => setValue(event.target.value.split(','))}
          />
        </FormControl>
      </HStack>
      <FormHelperText mt={5}>{generateFilterText()}</FormHelperText>
    </Box>
  );
};

const renderFilterConfigSwitch = (
  filter: FilterConfig,
  availableFields: CreditAgreement.Config.ConcentrationLimit.Calculation.AvailableField[],
  onChange: (newValue: FilterConfig) => void,
) => {
  const operation = filter.operation;
  switch (operation) {
    case 'orderBy':
      return (
        <OrderByConfigEditor
          availableFields={availableFields}
          filter={filter}
          onUpdate={onChange}
        />
      );
    case 'orderBySum':
      return (
        <OrderBySumConfigEditor
          availableFields={availableFields}
          filter={filter}
          onUpdate={onChange}
        />
      );
    case 'notEqual':
    case 'equals':
      return (
        <EqualsConfigEditor
          availableFields={availableFields}
          filter={filter}
          onUpdate={onChange}
        />
      );

    case 'lessThan':
    case 'greaterThan':
    case 'greaterThanOrEqual':
    case 'lessThanOrEqual':
      return (
        <LessThanOrEqualConfigEditor
          availableFields={availableFields}
          filter={filter}
          onUpdate={onChange}
        />
      );
    case 'inList':
    case 'notInList':
      return (
        <InListConfigEditor
          filter={filter}
          availableFields={availableFields}
          onUpdate={onChange}
        />
      );
    default:
      // return finleyAssertUnreachable(operation);
      // cant assert because JSONEditor allows invalid values.
      return null;
  }
};

export const FiltersViewer = ({
  filter,
  availableFields,
  onChange,
  onDelete,
}: {
  filter: CreditAgreement.Config.ConcentrationLimit.Calculation.Filters.FilterConfig;
  availableFields: CreditAgreement.Config.ConcentrationLimit.Calculation.AvailableField[];
  onChange: (newValue: FilterConfig) => void;
  onDelete: () => void;
}) => {
  // Function to update the applyToDenominator property
  const toggleApplyToDenominator = (checked: boolean) => {
    if (checked) {
      onChange({ ...filter, applyToDenominator: true });
    } else {
      const { applyToDenominator: _unused, ...rest } = filter;
      onChange(rest);
    }
  };

  return (
    <Box position="relative">
      {/* Container with relative positioning */}
      <DeleteIconButton
        ariaLabel="delete filter"
        label="delete filter"
        onClick={() => onDelete()}
        position="absolute" // Positioning the button absolutely
        top={0} // Aligning to the top
        right={0} // Aligning to the right
        zIndex="docked" // Ensuring it stays on top
      />
      {renderFilterConfigSwitch(filter, availableFields, onChange)}

      {/* Render applyToDenominator checkbox if the property exists */}
      <Checkbox
        isChecked={filter.applyToDenominator}
        onChange={(e) => toggleApplyToDenominator(e.target.checked)}
        ml={4}
        mb={4}
      >
        <ConcentrationCalcFieldLabel
          label="Apply filter to Denominator as well"
          tooltip="Typically filters are used to select receivables in numerator. If
        checked, this filter will be applied to the denominator as well. This is
        rarely used. If you are not sure, leave it unchecked."
        />
      </Checkbox>
    </Box>
  );
};

export const FiltersControl = ({
  filters,
  availableFields,
  onChange,
}: {
  filters: CreditAgreement.Config.ConcentrationLimit.Calculation.Filters.FilterConfig[];
  availableFields: CreditAgreement.Config.ConcentrationLimit.Calculation.AvailableField[];
  onChange: (
    newValue: CreditAgreement.Config.ConcentrationLimit.Calculation.Filters.FilterConfig[],
  ) => void;
}) => {
  const [currentFilters, setCurrentFilters] = React.useState<
    CreditAgreement.Config.ConcentrationLimit.Calculation.Filters.FilterConfig[]
  >(filters);

  const filterOperations = makeSelectOptions(
    CreditAgreement.Config.ConcentrationLimit.Calculation.Filters
      .OperationTypeIterable,
  );
  const [operationType, setOperationType] = React.useState<OperationType>(
    'orderBy',
  );

  useEffect(() => {
    setCurrentFilters(filters);
  }, [filters]);

  const updateFilters = (newFilters: FilterConfig[]) => {
    setCurrentFilters(newFilters);
    onChange(newFilters);
  };

  const addFilter = () =>
    updateFilters([createNewFilter(operationType), ...currentFilters]);
  const removeFilter = (index: number) =>
    updateFilters(currentFilters.filter((_, i) => i !== index));

  const updateFilter = (index: number, newFilter: FilterConfig) => {
    const newFilters = currentFilters.map((f, i) =>
      i === index ? newFilter : f,
    );
    updateFilters(newFilters);
  };

  const operationHelp: {
    [key in CreditAgreement.Config.ConcentrationLimit.Calculation.Filters.OperationType]: string;
  } = {
    orderBy:
      'Add `orderBy` filter. This orders receivables by a field, and lets you pick the N top values.',
    orderBySum:
      'Add `orderBySum` filter. This lets you group receivables by a field, order by sum of its values, then pick the N top values.',

    equals:
      'Add `equals` filter. This lets you pick receivables where its field equals a specific configured value.',
    notEqual:
      'Add `notEqual` filter. This lets you pick receivables where its field does not equals a specific configured value.',
    lessThan:
      'Add `lessThan` filter. This lets you pick receivables where its given field is less than a configured value.',
    greaterThan:
      'Add `greaterThan` filter. This lets you pick receivables where its given field is greater than a configured value.',
    greaterThanOrEqual:
      'Add `greaterThan` filter. This lets you pick receivables where its given field is greater or equal to a configured value.',
    lessThanOrEqual:
      'Add `lessThanOrEqual` filter. This lets you pick receivables where its given field is less or equal to a configured value.',
    inList:
      'Add `inList` filter. This lets you pick receivables where its given field matches one of the configured values in a list.',
    notInList:
      'Add `notInList` filter. This lets you pick receivables where its given field does not match any of the configured values in a list.',
  };

  return (
    <Box mb={4}>
      <HStack>
        <FormLabel fontSize="sm" m={1}>
          Filters
        </FormLabel>
        <AddIconButton
          ariaLabel="Add Filter"
          label={`${operationHelp[operationType]}`}
          onClick={() => addFilter()}
        />
        <Select<OperationType>
          value={operationType}
          onChange={(val) => {
            if (val) {
              setOperationType(val);
            }
          }}
          options={filterOperations}
        />{' '}
      </HStack>

      <VStack mt={2} spacing={4} align="stretch">
        {currentFilters.map((filter, index) => (
          // eslint-disable-next-line react/no-array-index-key
          <Box key={`filter#${index}`} border="1px solid gray">
            <FiltersViewer
              filter={filter}
              availableFields={availableFields}
              onChange={(f) => updateFilter(index, f)}
              onDelete={() => removeFilter(index)}
            />
          </Box>
        ))}
      </VStack>
    </Box>
  );
};
